/**
 * AdminEditGrantsReview.jsx — King + High King queue for pending
 * "extra edit" grant requests. Feb 12 2026 user spec: *"request edit
 * from High King or King. They can grant edit if found useful"*.
 *
 * UX:
 *   - List of pending requests with writer label, item title, reason,
 *     timestamp.
 *   - Approve / Deny buttons with optional decision-note text input.
 *   - Refresh every 30s so the queue stays live.
 */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Loader2, ArrowLeft, Crown, CheckCircle2, XCircle, RefreshCw, Inbox } from 'lucide-react';
import { authFetch } from '../utils/authUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUSES = [
  { id: 'pending',  label: 'Pending',  tone: 'bg-amber-400/10 border-amber-400/40 text-amber-200' },
  { id: 'approved', label: 'Approved', tone: 'bg-emerald-400/10 border-emerald-400/40 text-emerald-200' },
  { id: 'denied',   label: 'Denied',   tone: 'bg-rose-400/10 border-rose-400/40 text-rose-200' },
];

const ITEM_KIND_LABEL = {
  audiobook_chapter:  'Audiobook chapter',
  audiobook_series:   'Audiobook series',
  cinematic_episode:  'Cinematic episode',
  cinematic_series:   'Cinematic series',
  text_series:        'Text series',
  text_episode:       'Text episode',
  book_series:        'Book series',
  personal_library:   'Personal library',
  content:            'Content',
};

const AdminEditGrantsReview = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState('pending');
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [noteByGrant, setNoteByGrant] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/api/admin/edit-grants/requests?status=${status}`);
      if (r.status === 403) {
        toast.error('Kings + High King only');
        navigate('/');
        return;
      }
      const d = await r.json().catch(() => ({}));
      setRequests(d.requests || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 30_000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const decide = async (grantId, decision) => {
    setBusyId(grantId);
    try {
      const note = (noteByGrant[grantId] || '').trim();
      const r = await authFetch(`${BACKEND_URL}/api/admin/edit-grants/${grantId}/${decision}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note: note || undefined }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        toast.error(d.detail || 'Could not save decision');
        return;
      }
      toast.success(decision === 'grant' ? 'Edit granted' : 'Request denied');
      load();
    } finally {
      setBusyId(null);
    }
  };

  return (
    <main className="sharp-bg min-h-screen px-4 py-6 max-w-3xl mx-auto pb-32" data-testid="admin-edit-grants-page">
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-white/60 hover:text-white mb-4"
        data-testid="edit-grants-back"
      >
        <ArrowLeft className="w-3.5 h-3.5" /> Back
      </button>

      <header className="mb-6">
        <p className="eyebrow text-amber-300 flex items-center gap-2">
          <Crown className="w-3 h-3" /> Royal review
        </p>
        <h1 className="font-display text-3xl sm:text-4xl font-black text-white tracking-tight mt-1">
          Edit Requests<span className="text-amber-300">.</span>
        </h1>
        <p className="text-xs text-white/60 mt-2 max-w-xl">
          Writers get one edit per item by default. When they've used it, they ask you for another.
          Approve when the change is genuinely useful — deny when it's frivolous.
        </p>
      </header>

      {/* Status tabs */}
      <div className="flex items-center gap-2 mb-4" data-testid="edit-grants-status-tabs">
        {STATUSES.map((s) => (
          <button
            key={s.id}
            type="button"
            onClick={() => setStatus(s.id)}
            className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border transition ${
              status === s.id
                ? s.tone
                : 'border-white/10 bg-white/[0.02] text-white/50 hover:border-white/30'
            }`}
            data-testid={`edit-grants-tab-${s.id}`}
          >
            {s.label}
          </button>
        ))}
        <button
          type="button"
          onClick={load}
          className="ml-auto inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-white/60 hover:text-white"
          data-testid="edit-grants-refresh"
        >
          <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      {/* List */}
      {loading && !requests.length ? (
        <p className="text-white/40 text-sm" data-testid="edit-grants-loading">Loading…</p>
      ) : requests.length === 0 ? (
        <div className="rounded-2xl border border-white/10 bg-[#12131A] p-8 text-center" data-testid="edit-grants-empty">
          <Inbox className="w-10 h-10 text-white/30 mx-auto mb-3" />
          <p className="text-white/60 text-sm">No {status} requests right now.</p>
        </div>
      ) : (
        <ul className="space-y-3" data-testid="edit-grants-list">
          {requests.map((r) => (
            <li
              key={r.grant_id}
              className="rounded-2xl border border-white/10 bg-[#12131A] p-4 space-y-2"
              data-testid={`edit-grant-row-${r.grant_id}`}
            >
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-[10px] uppercase tracking-[0.3em] text-white/40">
                    {ITEM_KIND_LABEL[r.item_kind] || r.item_kind}
                    {r.user_country && <span className="ml-2 text-amber-300">· {r.user_country}</span>}
                  </p>
                  <h3 className="font-display text-base font-black text-white truncate">{r.item_title}</h3>
                  <p className="text-[11px] text-white/55 truncate">by {r.user_label}</p>
                </div>
                <p className="text-[10px] text-white/40 tabular-nums whitespace-nowrap">
                  {r.requested_at ? new Date(r.requested_at).toLocaleString() : ''}
                </p>
              </div>

              {r.reason && (
                <div className="rounded-xl bg-white/[0.03] border border-white/5 p-3">
                  <p className="text-[10px] uppercase tracking-widest text-white/40 mb-1">Reason</p>
                  <p className="text-sm text-white/85 whitespace-pre-wrap">{r.reason}</p>
                </div>
              )}

              {r.status === 'pending' ? (
                <>
                  <input
                    value={noteByGrant[r.grant_id] || ''}
                    onChange={(e) => setNoteByGrant((m) => ({ ...m, [r.grant_id]: e.target.value }))}
                    placeholder="Optional note to the scribe"
                    className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-1.5 text-xs text-white placeholder-white/30 focus:border-amber-400/60 focus:outline-none"
                    maxLength={300}
                    data-testid={`edit-grant-note-${r.grant_id}`}
                  />
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => decide(r.grant_id, 'grant')}
                      disabled={busyId === r.grant_id}
                      className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full bg-emerald-400 hover:bg-emerald-300 disabled:opacity-60 text-black font-black px-4 py-2 text-xs uppercase tracking-widest"
                      data-testid={`edit-grant-approve-${r.grant_id}`}
                    >
                      {busyId === r.grant_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                      Grant
                    </button>
                    <button
                      type="button"
                      onClick={() => decide(r.grant_id, 'deny')}
                      disabled={busyId === r.grant_id}
                      className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full bg-rose-400/20 hover:bg-rose-400/30 disabled:opacity-60 text-rose-200 border border-rose-400/40 font-black px-4 py-2 text-xs uppercase tracking-widest"
                      data-testid={`edit-grant-deny-${r.grant_id}`}
                    >
                      <XCircle className="w-3.5 h-3.5" />
                      Deny
                    </button>
                  </div>
                </>
              ) : (
                <div className="rounded-xl bg-white/[0.03] border border-white/5 p-2">
                  <p className="text-[10px] uppercase tracking-widest text-white/40">
                    {r.status === 'approved' ? 'Granted' : 'Denied'} · {r.decided_at ? new Date(r.decided_at).toLocaleString() : ''}
                  </p>
                  {r.decision_note && (
                    <p className="text-xs text-white/70 mt-1">{r.decision_note}</p>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </main>
  );
};

export default AdminEditGrantsReview;
